import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Shield, 
  Users, 
  Stethoscope, 
  Pill, 
  Calendar, 
  HeartPulse, 
  CheckCircle2,
  ArrowRight,
  Building2,
  TrendingUp,
  Heart,
  Scale,
  Award,
  ShieldCheck,
  Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';

const HERO_IMAGE = "https://images.unsplash.com/photo-1758272133786-ee98adcc6837?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHx5b3VuZyUyMGFkdWx0cyUyMGRpdmVyc2UlMjBncm91cCUyMHNtaWxpbmclMjB1cmJhbnxlbnwwfHx8fDE3NzA5MjUzODh8MA&ixlib=rb-4.1.0&q=85";
const COMMUNITY_IMAGE = "https://images.unsplash.com/photo-1758272134088-d3d58d939d12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHx5b3VuZyUyMGFkdWx0cyUyMGRpdmVyc2UlMjBncm91cCUyMHNtaWxpbmclMjB1cmJhbnxlbnwwfHx8fDE3NzA5MjUzODh8MA&ixlib=rb-4.1.0&q=85";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export const HomePage = () => {
  const { t } = useTranslation();

  const cboFeatures = [
    {
      icon: Shield,
      title: t('home.specializedHealthPlans'),
      description: t('home.specializedHealthPlansDesc'),
    },
    {
      icon: Pill,
      title: t('home.copayPAF'),
      description: t('home.copayPAFDesc'),
    },
    {
      icon: TrendingUp,
      title: t('home.maximizeReimbursements'),
      description: t('home.maximizeReimbursementsDesc'),
    },
    {
      icon: Users,
      title: t('home.highVolumeEnrollment'),
      description: t('home.highVolumeEnrollmentDesc'),
    },
  ];

  const individualFeatures = [
    {
      icon: Users,
      title: t('home.personalizedPlan'),
      description: t('home.personalizedPlanDesc'),
    },
    {
      icon: Stethoscope,
      title: t('home.inNetworkDoctors'),
      description: t('home.inNetworkDoctorsDesc'),
    },
    {
      icon: Pill,
      title: t('home.prescriptionCoverage'),
      description: t('home.prescriptionCoverageDesc'),
    },
    {
      icon: Calendar,
      title: t('home.enrollmentSupport'),
      description: t('home.enrollmentSupportDesc'),
    }
  ];

  const coverageTypes = [
    { name: t('home.communityOrganizations'), icon: Building2, path: '/community-organizations', primary: true },
    { name: t('home.healthInsurance'), icon: HeartPulse, path: '/health-plans', primary: false },
    { name: t('home.dentalVision'), icon: Heart, path: '/dental-vision', primary: false },
    { name: t('home.medicare'), icon: ShieldCheck, path: '/medicare', primary: false, desc: t('home.medicareDesc') },
    { name: t('home.groupBenefits'), icon: Briefcase, path: '/small-business', primary: false, desc: t('home.groupBenefitsDesc') },
  ];

  return (
    <div className="overflow-hidden" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-slate-900 min-h-[85vh] flex items-center" data-testid="hero-section">
        <div className="container-custom py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial="initial"
              animate="animate"
              variants={stagger}
              className="text-center lg:text-left"
            >
              <motion.div 
                variants={fadeInUp}
                className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500/20 text-sky-400 rounded-full text-sm font-medium mb-6"
              >
                <Building2 className="h-4 w-4" />
                {t('home.badge')}
              </motion.div>
              
              <motion.h1 
                variants={fadeInUp}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6"
              >
                {t('home.heroTitle')}{' '}
                <span className="text-sky-400">{t('home.heroTitleHighlight')}</span>
              </motion.h1>
              
              <motion.p 
                variants={fadeInUp}
                className="text-lg text-slate-400 mb-8 max-w-xl mx-auto lg:mx-0"
              >
                {t('home.heroDescription')}
              </motion.p>
              
              <motion.div 
                variants={fadeInUp}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              >
                <Link to="/community-organizations">
                  <Button className="bg-sky-500 hover:bg-sky-600 text-white h-12 px-8 rounded-full font-semibold" data-testid="hero-cta-cbo">
                    {t('home.ctaPartner')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 h-12 px-8 rounded-full" data-testid="hero-cta-contact">
                    {t('home.ctaConsultation')}
                  </Button>
                </Link>
              </motion.div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={COMMUNITY_IMAGE} 
                  alt="Diverse community we serve"
                  className="w-full h-auto object-cover aspect-[4/3]"
                  data-testid="hero-image"
                />
                {/* Floating Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="absolute bottom-6 left-6 right-6 sm:left-auto sm:right-6 sm:w-64 bg-white/95 backdrop-blur-md rounded-2xl p-4 shadow-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-sky-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-sky-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{t('home.floatingCard')}</p>
                      <p className="text-xs text-slate-500">{t('home.floatingCardSub')}</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CBO Features Section */}
      <section className="section-spacing bg-white" data-testid="cbo-features-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 rounded-full text-sm font-medium mb-4">
              <Building2 className="h-4 w-4" />
              {t('home.cboSectionBadge')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('home.cboSectionTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('home.cboSectionSubtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {cboFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl border border-slate-100 card-shadow feature-card"
                data-testid={`cbo-feature-card-${index}`}
              >
                <div className="h-12 w-12 bg-slate-900 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/community-organizations">
              <Button className="btn-primary" data-testid="cbo-learn-more">
                {t('home.learnCBO')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Coverage Types */}
      <section className="section-spacing gradient-subtle" data-testid="coverage-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('home.ourServices')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('home.ourServicesSubtitle')}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {coverageTypes.map((coverage, index) => (
              <motion.div
                key={coverage.path}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link 
                  to={coverage.path}
                  className={`block p-8 rounded-2xl border card-shadow hover:border-sky-200 transition-all group ${
                    coverage.primary 
                      ? 'bg-slate-900 border-slate-900 hover:bg-slate-800' 
                      : 'bg-white border-slate-100'
                  }`}
                  data-testid={`coverage-card-${index}`}
                >
                  <div className={`h-14 w-14 rounded-2xl flex items-center justify-center mb-6 ${
                    coverage.primary 
                      ? 'bg-sky-500' 
                      : 'bg-slate-900 group-hover:bg-sky-500'
                  } transition-colors`}>
                    <coverage.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className={`text-xl font-semibold mb-2 ${
                    coverage.primary ? 'text-white' : 'text-slate-900'
                  }`}>{coverage.name}</h3>
                  <p className={`text-sm mb-4 ${
                    coverage.primary ? 'text-slate-300' : 'text-slate-600'
                  }`}>
                    {coverage.primary 
                      ? t('home.communityOrganizationsDesc')
                      : (coverage.desc || t('home.findPlan'))}
                  </p>
                  <span className={`inline-flex items-center font-medium text-sm ${
                    coverage.primary ? 'text-sky-400' : 'text-sky-500'
                  } group-hover:gap-2 transition-all`}>
                    {t('home.learnMore')} <ArrowRight className="h-4 w-4 ml-1" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Individual Coverage Section */}
      <section className="section-spacing bg-white" data-testid="individual-features-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 text-sky-600 rounded-full text-sm font-medium mb-4">
              <Users className="h-4 w-4" />
              {t('home.forIndividuals')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('home.personalGuidance')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('home.personalGuidanceSubtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {individualFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl border border-slate-100 card-shadow feature-card"
                data-testid={`feature-card-${index}`}
              >
                <div className="h-12 w-12 bg-sky-50 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-sky-500" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance-Driven Operations */}
      <section className="py-16 gradient-subtle" data-testid="compliance-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 rounded-full text-sm font-medium mb-6">
                <Scale className="h-4 w-4" />
                Regulatory Standards
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Compliance-Driven Operations
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                Calyvra Insurance operates under strict federal and state regulatory standards, with processes 
                designed to ensure ethical enrollment practices, protection of consumer information, and clear 
                operational boundaries with partner organizations.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Recognition Section */}
      <section className="py-16 bg-white" data-testid="recognition-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 text-sky-600 rounded-full text-sm font-medium mb-6">
                <Award className="h-4 w-4" />
                {t('home.recognitionBadge')}
              </div>
              <div className="mb-6">
                <img 
                  src="/circle-of-champions-2026.png" 
                  alt="Marketplace Circle of Champions 2026 Badge" 
                  className="h-40 md:h-48 mx-auto"
                  data-testid="champions-badge"
                />
              </div>
              <p className="text-slate-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                {t('home.recognitionText')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900" data-testid="cta-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={HERO_IMAGE} 
                alt="Community protected by health coverage"
                className="rounded-2xl shadow-xl"
                data-testid="cta-image"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-center lg:text-left"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                {t('home.readyToStart')}
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                {t('home.readyToStartDesc')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/community-organizations">
                  <Button className="bg-sky-500 hover:bg-sky-600 text-white h-12 px-8 rounded-full font-semibold" data-testid="cta-cbo-btn">
                    {t('home.cboPartnership')}
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 h-12 px-8 rounded-full" data-testid="cta-quote-btn">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-slate-50 border-y border-slate-100" data-testid="trust-section">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex items-center gap-3">
              <Building2 className="h-6 w-6 text-sky-500" />
              <span className="text-slate-600 font-medium">{t('home.sustainableCoverageSolutions')}</span>
            </div>
            <div className="flex items-center gap-3">
              <HeartPulse className="h-6 w-6 text-sky-500" />
              <span className="text-slate-600 font-medium">{t('home.lgbtqAffirming')}</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-sky-500" />
              <span className="text-slate-600 font-medium">{t('home.noPressureSales')}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
